import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Upload, FileText, Landmark } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export default function EntitiesSettings() {
  const { orgId, familyId } = useTenant();

  // Fetch entities
  const { data: entities = [] } = useQuery({
    queryKey: ['entities', orgId, familyId],
    queryFn: async () => {
      let query = supabase.from('entities').select('*');
      
      if (familyId) {
        query = query.eq('family_id', familyId);
      } else if (orgId) {
        query = query.eq('org_id', orgId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!(orgId || familyId),
  });

  // Fetch accounts
  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts', orgId, familyId],
    queryFn: async () => {
      let query = supabase.from('accounts').select('*');
      
      if (familyId) {
        query = query.eq('family_id', familyId);
      } else if (orgId) {
        query = query.eq('org_id', orgId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!(orgId || familyId),
  });


  return (
    <div className="flex-1 space-y-4 p-8">
      
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Entities</h1>
        <p className="text-muted-foreground">
          Manage entity structure, documents, and bank accounts
        </p>
      </div>

      <Tabs defaultValue="structure" className="space-y-4">
        <TabsList>
          <TabsTrigger value="structure">Structure</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="accounts">Bank Accounts</TabsTrigger>
        </TabsList>

        {/* Structure Tab */}
        <TabsContent value="structure" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Entity Structure
                  </CardTitle>
                  <CardDescription>
                    Visual representation of your family office structure
                  </CardDescription>
                </div>
                <Button>
                  <Plus className="h-4 w-4" />
                  Add Entity
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Structure Diagram */}
              <div className="space-y-6">
                {/* Root Level */}
                <div className="flex flex-col items-center">
                  <div className="border-2 border-primary rounded-lg p-4 bg-card min-w-[250px]">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">Family Office</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">Type: Holding Company</p>
                    <p className="text-sm text-muted-foreground">Jurisdiction: Delaware, US</p>
                  </div>
                  <div className="h-8 w-0.5 bg-border" />
                </div>

                {/* Second Level - Entities from DB */}
                <div className="flex justify-center gap-8 flex-wrap">
                  {entities.length > 0 ? (
                    entities.map((entity) => (
                      <div key={entity.id} className="flex flex-col items-center">
                        <div className="border rounded-lg p-4 bg-card min-w-[200px] hover:border-primary transition-colors cursor-pointer">
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="h-4 w-4" />
                            <h4 className="font-medium">{entity.name}</h4>
                          </div>
                          <Badge variant="outline" className="mb-2">{entity.type}</Badge>
                          <p className="text-xs text-muted-foreground">Entity ID: {entity.id.slice(0, 8)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No entities yet. Click "Add Entity" to create your first entity.</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Entities Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Entities</CardTitle>
              <CardDescription>Complete list of entities in your structure</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entity Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Jurisdiction</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entities.length > 0 ? (
                    entities.map((entity) => (
                      <TableRow key={entity.id}>
                        <TableCell className="font-medium">{entity.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{entity.type}</Badge>
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>{new Date(entity.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">Edit</Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No entities found. Add your first entity to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Document Repository
                  </CardTitle>
                  <CardDescription>
                    Entity formation documents, agreements, and legal files
                  </CardDescription>
                </div>
                <Button>
                  <Upload className="h-4 w-4" />
                  Upload Document
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document Name</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No documents uploaded yet. Upload entity documents to get started.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Accounts Tab */}
        <TabsContent value="accounts" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Landmark className="h-5 w-5" />
                    Bank Accounts
                  </CardTitle>
                  <CardDescription>
                    Bank accounts associated with each entity
                  </CardDescription>
                </div>
                <Button>
                  <Plus className="h-4 w-4" />
                  Add Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account Number</TableHead>
                    <TableHead>Custodian</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.length > 0 ? (
                    accounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className="font-medium font-mono">{account.number}</TableCell>
                        <TableCell>{account.custodian}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{account.type}</Badge>
                        </TableCell>
                        <TableCell>{account.ccy}</TableCell>
                        <TableCell>{account.entity_id ? account.entity_id.slice(0, 8) : '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">Edit</Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No bank accounts found. Add your first account to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
